/**
 * One door to the master tables instead of eleven in the sidebar.
 *
 * These are dropdown value lists - "what tool types exist", "what materials do
 * we use". Almost nobody navigates to them on purpose: values get added through
 * the ＋ beside each field while filling in a mould, which is the moment you
 * discover one is missing. Giving each list a permanent sidebar entry put the
 * least-visited screens in the product at eye level and pushed the catalog down
 * a scroll.
 *
 * They stay reachable at the same URLs; only the sidebar is one line now.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { Page } from '../../components/layout/Page.js';
import { Card, CardBody } from '../../components/ui/primitives.js';
import { MASTER_DEFINITIONS } from '../../components/layout/navigation.js';
import { db } from '../../db/schema.js';

export function MasterHubPage() {
  // One pass over the tables rather than a live query each: eleven independent
  // subscriptions to count eleven small tables is a lot of machinery for a
  // number.
  const counts = useLiveQuery(async () => {
    const entries = await Promise.all(
      MASTER_DEFINITIONS.map(async (d) => {
        const rows = (await db.table(d.table).toArray()) as Array<{ isActive?: boolean }>;
        return [d.table, rows.filter((r) => r.isActive !== false).length] as const;
      }),
    );
    return Object.fromEntries(entries) as Record<string, number>;
  }, []);

  return (
    <Page
      title="Master Data"
      description="The values that fill the dropdowns on a mould. Add them here, or with the ＋ beside any field."
    >
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {MASTER_DEFINITIONS.map((d) => {
          const Icon = d.icon;
          const count = counts?.[d.table];
          return (
            <Link
              key={d.table}
              to={`/masters/${d.path}`}
              className="rounded-xl transition-colors hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            >
              <Card className="h-full">
                <CardBody className="flex items-start gap-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <h2 className="font-medium">{d.title}</h2>
                      <span className="shrink-0 text-sm tabular text-muted-foreground">
                        {count ?? '-'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-muted-foreground">{d.description}</p>
                  </div>
                </CardBody>
              </Card>
            </Link>
          );
        })}
      </div>
    </Page>
  );
}
